/**
 * WebSocketService — Real-time market data via WebSocket with HTTP fallback.
 * Connects to ws(s)://host/ws/ and subscribes to match topics.
 * If WS unavailable, components fall back to HTTP polling automatically.
 */

class WebSocketManager {
  constructor() {
    this.ws = null;
    this.listeners = {};
    this.reconnectTimer = null;
    this.subscriptions = new Map();
    this.connected = false;
    this.connectAttempts = 0;
    this.maxReconnectDelay = 30000;
    this.cache = {};
  }

  connect() {
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/api/ws`;

    try {
      this.ws = new WebSocket(wsUrl);
    } catch (e) {
      // WS connect failed — will retry via scheduleReconnect
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      // Connected
      this.connected = true;
      this.connectAttempts = 0;
      clearTimeout(this.reconnectTimer);
      // Re-subscribe all pending topics
      this.subscriptions.forEach((sub) => {
        try { this.ws.send(JSON.stringify({ action: 'subscribe', topic: sub.topic, matchId: sub.matchId })); } catch {}
      });
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        const { topic, data } = msg;
        if (topic) {
          // Cache latest data per topic
          this.cache[topic] = data;
          if (this.listeners[topic]) {
            this.listeners[topic].forEach(cb => {
              try { cb(data); } catch (e) { console.error('[WS] Listener error:', e); }
            });
          }
        }
      } catch {}
    };

    this.ws.onclose = () => {
      this.connected = false;
      // Disconnected
      this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      this.connected = false;
      try { this.ws.close(); } catch {}
    };
  }

  scheduleReconnect() {
    clearTimeout(this.reconnectTimer);
    this.connectAttempts++;
    // Exponential backoff: 3s, 6s, 12s, max 30s
    const delay = Math.min(3000 * Math.pow(2, this.connectAttempts - 1), this.maxReconnectDelay);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  subscribe(topic, matchId, callback) {
    const key = `${topic}|${matchId}`;
    if (!this.listeners[topic]) this.listeners[topic] = new Set();
    this.listeners[topic].add(callback);
    this.subscriptions.set(key, { topic, matchId });

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ action: 'subscribe', topic, matchId }));
    }
    // If not connected yet, onopen handler will send all pending subscriptions
  }

  unsubscribe(topic, matchId, callback) {
    if (this.listeners[topic]) {
      if (callback) this.listeners[topic].delete(callback);
      if (!callback || this.listeners[topic].size === 0) delete this.listeners[topic];
    }
    const key = `${topic}|${matchId}`;
    this.subscriptions.delete(key);

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ action: 'unsubscribe', topic, matchId }));
    }
  }

  getCache(topic) {
    return this.cache[topic] || null;
  }

  isConnected() {
    return this.connected && this.ws?.readyState === WebSocket.OPEN;
  }

  disconnect() {
    clearTimeout(this.reconnectTimer);
    this.subscriptions.clear();
    this.listeners = {};
    if (this.ws) {
      try { this.ws.close(); } catch {}
      this.ws = null;
    }
    this.connected = false;
  }
}

// Global singleton
if (!window.__wsService) {
  window.__wsService = new WebSocketManager();
}

export const wsService = window.__wsService;
